/**
 * Canonical profile media resolution utilities.
 * 
 * Handles all known photo/video field shapes across the app:
 * - String URLs: "https://..." or "/api/uploads/..."
 * - Object arrays: [{ url: "..." }, { src: "..." }, { photo_url: "..." }]
 * - Keyed objects: { "photo-id-1": { url: "..." } }
 * - Mixed formats from different API endpoints
 */

const API_URL = window.location.origin;

/**
 * Check if a URL is absolute (starts with http:// or https://)
 */
export function isAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('http://') || url.startsWith('https://');
}

/**
 * Ensure URL is absolute by prepending backend URL if needed
 */
export function ensureAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (isAbsoluteUrl(url)) return url;
  // Handle relative URLs - prepend backend URL
  if (url.startsWith('/')) {
    return `${API_URL}${url}`;
  }
  return url;
}

/**
 * Extract URL from a photo item that could be:
 * - A string URL
 * - An object with url, src, photo_url, public_url, or key
 */
function extractUrlFromPhotoItem(item) {
  if (!item) return null;
  
  // Direct string URL
  if (typeof item === 'string') {
    return item.trim() || null;
  }
  
  // Object with various URL field names
  if (typeof item === 'object') {
    const url = item.url || item.src || item.photo_url || item.public_url || item.uri;
    if (url && typeof url === 'string') {
      return url.trim() || null;
    }
  }
  
  return null;
}

/**
 * Get profile photos from any profile object, regardless of field naming.
 * Checks: profile_photos, profilePhotos, photos, images, media.photos
 */
export function getProfilePhotos(profile) {
  if (!profile) return [];
  
  // Check all possible field names in order of preference
  // IMPORTANT: Use .length check — empty arrays are truthy in JS and would
  // short-circuit the || chain, preventing fallback to populated fields.
  const photos = 
    (Array.isArray(profile.profile_photos) && profile.profile_photos.length > 0 ? profile.profile_photos : null) ||
    (Array.isArray(profile.profilePhotos) && profile.profilePhotos.length > 0 ? profile.profilePhotos : null) ||
    (Array.isArray(profile.photos) && profile.photos.length > 0 ? profile.photos : null) ||
    (Array.isArray(profile.images) && profile.images.length > 0 ? profile.images : null) ||
    (Array.isArray(profile.media?.photos) && profile.media.photos.length > 0 ? profile.media.photos : null) ||
    [];
  
  // Handle keyed object format: { "photo-id": { url: "..." } }
  if (photos && typeof photos === 'object' && !Array.isArray(photos)) {
    return Object.values(photos);
  }
  
  // Ensure it's an array
  if (!Array.isArray(photos)) return [];
  
  // Sort by position if available
  return [...photos].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
}

/**
 * Resolve the best profile photo URL from any profile shape.
 * 
 * Priority:
 * 1. main_photo_url / mainPhotoUrl (explicit main photo)
 * 2. photo_url (simplified single photo field)
 * 3. avatar_url (common avatar field)
 * 4. profile_photo_url (another legacy variant)
 * 5. main_photo (string variant)
 * 6. First item from photos[] array (handling string or object formats)
 * 7. First item from profile_photos[] array
 * 8. Nested user object (for profiles with embedded user)
 * 
 * @param {Object} profile - Profile object from API
 * @param {Object} options - Options
 * @param {boolean} options.absolute - If true, ensure returned URL is absolute (default: true)
 * @returns {string|null} - Photo URL or null if none found
 */
export function resolveProfilePhotoUrl(profile, options = { absolute: true }) {
  if (!profile || typeof profile !== 'object') {
    return null;
  }

  let url = null;

  // 1. Check explicit main photo fields first (in priority order)
  if (profile.main_photo_url && typeof profile.main_photo_url === 'string') {
    url = profile.main_photo_url.trim();
  } else if (profile.mainPhotoUrl && typeof profile.mainPhotoUrl === 'string') {
    url = profile.mainPhotoUrl.trim();
  } else if (profile.photo_url && typeof profile.photo_url === 'string') {
    url = profile.photo_url.trim();
  } else if (profile.avatar_url && typeof profile.avatar_url === 'string') {
    url = profile.avatar_url.trim();
  } else if (profile.profile_photo_url && typeof profile.profile_photo_url === 'string') {
    url = profile.profile_photo_url.trim();
  } else if (profile.main_photo && typeof profile.main_photo === 'string') {
    url = profile.main_photo.trim();
  }

  // 2. Try photos array
  if (!url) {
    const photos = getProfilePhotos(profile);
    
    if (photos.length > 0) {
      // Find first item marked as main/primary, or just first item
      const mainPhoto = photos.find(p => p?.is_main === true || p?.is_primary === true);
      const firstPhoto = mainPhoto || photos[0];
      url = extractUrlFromPhotoItem(firstPhoto);
    }
  }

  // 3. Try nested user object (for profile objects with embedded user)
  if (!url && profile.user && typeof profile.user === 'object') {
    return resolveProfilePhotoUrl(profile.user, options);
  }

  // Validate we got something
  if (!url) {
    // DEV warning to catch new shapes
    if (process.env.NODE_ENV === 'development') {
      const photos = getProfilePhotos(profile);
      if (photos.length > 0) {
        console.warn('[profileMedia] Profile has photos but resolver returned null:', {
          photosCount: photos.length,
          firstPhoto: photos[0],
          main_photo_url: profile.main_photo_url
        });
      }
    }
    return null;
  }

  // Ensure absolute URL if requested
  return options.absolute ? ensureAbsoluteUrl(url) : url;
}

/**
 * Get the primary photo URL (alias for resolveProfilePhotoUrl for backward compatibility)
 */
export function getPrimaryPhotoUrl(profile) {
  return resolveProfilePhotoUrl(profile, { absolute: true });
}

/**
 * Alias for resolveProfilePhotoUrl - matches naming in other utilities
 */
export function resolveMainPhoto(profileOrUser) {
  return resolveProfilePhotoUrl(profileOrUser, { absolute: true });
}

/**
 * Resolve avatar URL with fallback to placeholder.
 * Convenience wrapper around resolveProfilePhotoUrl.
 * 
 * @param {Object} profileOrUser - Profile or user object
 * @param {string} fallback - Fallback URL (default: null for component to handle)
 * @returns {string|null} - Photo URL or fallback
 */
export function resolveAvatarUrl(profileOrUser, fallback = null) {
  return resolveProfilePhotoUrl(profileOrUser, { absolute: true }) || fallback;
}

/**
 * Resolve profile video intro URL from any profile shape.
 * 
 * @param {Object} profile - Profile object from API
 * @param {Object} options - Options
 * @param {boolean} options.absolute - If true, ensure returned URL is absolute
 * @returns {string|null} - Video URL or null if none found
 */
export function resolveProfileVideoUrl(profile, options = { absolute: true }) {
  if (!profile || typeof profile !== 'object') {
    return null;
  }

  let url = null;

  // Check common video field names
  const videoUrl = 
    profile.video_intro_url ||
    profile.videoIntroUrl ||
    profile.video_url ||
    profile.intro_video_url;

  if (videoUrl && typeof videoUrl === 'string') {
    url = videoUrl.trim();
  }

  // Try videos array
  if (!url) {
    const videos = profile.videos || profile.media?.videos;
    if (Array.isArray(videos) && videos.length > 0) {
      url = extractUrlFromPhotoItem(videos[0]);
    }
  }

  if (!url) return null;

  return options.absolute ? ensureAbsoluteUrl(url) : url;
}

/**
 * Get profile video intro URL (alias for backward compatibility)
 */
export function getVideoIntroUrl(profile) {
  return resolveProfileVideoUrl(profile, { absolute: true });
}

/**
 * Count photos in profile.
 */
export function getPhotoCount(profile) {
  return getProfilePhotos(profile).length;
}

/**
 * Get display name from profile with fallback
 * 
 * @param {Object} profile - Profile object
 * @param {string} fallback - Fallback name if none found
 * @returns {string} - Display name
 */
export function resolveDisplayName(profile, fallback = 'Someone') {
  if (!profile) return fallback;
  
  // Handle names array (couple profiles)
  if (Array.isArray(profile.names) && profile.names.length > 0) {
    return profile.names.join(' & ');
  }
  
  // Single display name
  if (profile.display_name) return profile.display_name;
  if (profile.displayName) return profile.displayName;
  if (profile.name) return profile.name;
  
  // First name fallback
  if (profile.first_name) return profile.first_name;
  if (profile.firstName) return profile.firstName;
  
  return fallback;
}

export default {
  getProfilePhotos,
  getPrimaryPhotoUrl,
  getVideoIntroUrl,
  getPhotoCount,
  resolveProfilePhotoUrl,
  resolveProfileVideoUrl,
  resolveDisplayName,
  resolveMainPhoto,
  resolveAvatarUrl,
  isAbsoluteUrl,
  ensureAbsoluteUrl,
};
